import React, { useContext, useImperativeHandle, useMemo, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { SNACKBAR_SEVERITY } from './const';
import Snackbar, { Props } from './index';

type SnackBarData = Omit<Props, 'severity' | 'onClose'>;
type ShowSnackBar = (data: Omit<Props, 'onClose'>) => void;
type SnackbarAction = (data: SnackBarData) => void;
interface SnackbarActions {
  showErrorSnackbar: SnackbarAction;
  showWarningSnackbar: SnackbarAction;
  showSuccessSnackbar: SnackbarAction;
  showInfoSnackbar: SnackbarAction;
}

const SnackbarContext = React.createContext<SnackbarActions | null>(null);

export const useSnackbarContext = () => useContext(SnackbarContext)!;

const SnackBarRef = ({ snackBarRef }: {
  snackBarRef: React.RefObject<{ show: ShowSnackBar }>;
}) => {
  const [data, setData] = useState<Omit<Props, 'onClose'> | null>(null);
  const showSnackBar: ShowSnackBar = (data) => setData(data);
  const onClose = () => setData(null);

  useImperativeHandle(snackBarRef, () => {
    return { show: showSnackBar };
  }, [showSnackBar]);

  return data ? <Snackbar {...data} open onClose={onClose} /> : null;
};

const SnackbarProvider = ({ children }: { children: React.ReactNode }) => {
  const snackBarRef = useRef<{ show: ShowSnackBar }>(null);
  const { t } = useTranslation();

  const actions = useMemo(() => {
    const showSnackBar = (data: Omit<Props, 'onClose'>) => snackBarRef.current?.show(data);

    return {
      showErrorSnackbar: (data: SnackBarData) => showSnackBar({
        ...data,
        severity: SNACKBAR_SEVERITY.ERROR,
        title: data.title || t('error')
      }),
      showWarningSnackbar: (data: SnackBarData) => showSnackBar({
        ...data,
        severity: SNACKBAR_SEVERITY.WARNING,
        title: data.title || t('warning')
      }),
      showSuccessSnackbar: (data: SnackBarData) => showSnackBar({
        ...data,
        severity: SNACKBAR_SEVERITY.SUCCESS,
        title: data.title || t('success')
      }),
      showInfoSnackbar: (data: SnackBarData) => showSnackBar({
        ...data,
        severity: SNACKBAR_SEVERITY.INFO,
        title: data.title || t('info')
      }),
    };
  }, [t, snackBarRef]);

  return (
    <SnackbarContext.Provider value={actions}>
      {children}
      <SnackBarRef snackBarRef={snackBarRef} />
    </SnackbarContext.Provider>
  );
};

export default SnackbarProvider;